/**
 * astroVastu — the thin FRONTEND callable seam for the L1A Astro-Vastu tab.
 *
 * Invokes the read-only backend callable `resolveAstroVastuForProject` (Forge)
 * and types its response. That callable runs the already-live astro stages —
 * `resolveAstroFamily` (roster + ascendant) → `astroBedroom` (Set-9 ideal-zone
 * port) → `astroAllocation` (reconcile vs the bedrooms marked in Per-property)
 * — and returns them to the staff page. It invents NO new astrology model.
 *
 * ── TOLERANCE CONTRACT (important) ────────────────────────────────────────────
 * The exact response shape is being finalised by Forge in parallel. This seam
 * uses a PROVISIONAL shape and is deliberately defensive: every field is
 * optional / nullable, the normaliser coerces anything unexpected to a safe
 * default, and a thrown/rejected call resolves to `null` (the screen renders its
 * empty/error state). A pending chart, a null entranceVerdict, a missing
 * allocation — none of these may crash the tab. See `normalizeResolveResponse`.
 *
 * Region + emulator wiring is inherited from the shared `functions` client
 * (asia-south1), identical to every other callable seam (geocode.ts, etc).
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/* ════════════════════ Provisional response contract ════════════════════ */

/** Per-member chart resolution state. `pending` degrades to a graceful row. */
export type ChartStatus = 'resolved' | 'pending';

/** An ideal bedroom zone for a member, with its class (ideal vs positive alt). */
export interface AstroIdealZone {
  /** Compass zone token, e.g. 'SW', 'NW', 'NE'. */
  zone: string;
  /** 'ideal' = the primary Set-9 zone; 'positive' = a charted alternative. */
  klass: 'ideal' | 'positive';
}

/** The achievable-in-THIS-home zone for a member (reconciled vs marked beds). */
export interface AstroAchievableZone {
  zone: string;
  /**
   * Forge's final severity vocabulary:
   *   'ok'          — clean match (green)
   *   'remedy'      — achievable but needs a remedy note (amber)
   *   'critical'    — achievable only as a critical compromise (red)
   *   'unspecified' — engine gave no severity (neutral)
   * A null `achievableZone` (not this field) = "not available".
   */
  severity: string;
}

/** One entrance pada row (from the engine's `acceptable` / `avoid` lists). */
export interface AstroEntrancePada {
  pada: string | null;
  zone: string | null;
  box: number | null;
  rashi: string | null;
}

/**
 * Entrance verdict — the CHART's ideal entrance pada/zone from
 * `/api/astro-vastu/generate` (`source:'chart-ideal'`). This is NOT yet a
 * reconciliation against the home's marked entrance — it is the astro-side ideal,
 * analogous to the ideal bedroom zone. `null` only when the chart is pending.
 */
export interface AstroEntranceVerdict {
  /** 'ideal-available' when a Box-4 entrance pada exists; else 'no-ideal'. */
  disposition: string;
  /** Short human-readable rationale, if the engine supplies one. */
  note?: string | null;
  /** Recommended entrance zone (16-zone label), null when no ideal pada. */
  recommendationZone?: string | null;
  /** Recommended entrance pada code (e.g. 'E4'), null when no ideal pada. */
  recommendationPada?: string | null;
  /** The box the recommended pada maps to on this chart, null when none. */
  recommendationBox?: number | null;
  /** Positive (acceptable) entrance padas on this chart. */
  acceptablePadas?: AstroEntrancePada[];
  /** Excluded (avoid) entrance padas on this chart. */
  avoidPadas?: AstroEntrancePada[];
  /** Stated reason when no ideal (Box-4) entrance pada exists. */
  noIdealReason?: string | null;
  /** Provenance tag; 'chart-ideal' = the astro ideal, not reconciled vs marked. */
  source?: string;
}

/**
 * One row of the Comparative Box Matrix (12 per member) from the engine's
 * `box_sign_map`. `direction` is the primary Set-7 display direction; `directions`
 * carries the full list and `lord` the sign lord — both additive for a richer table.
 */
export interface AstroBoxRow {
  /** Box number 1..12 (Box 1 = the Ascendant sign). */
  box: number;
  /** Life-context label for the box (e.g. 'Building front and facing'). */
  lifeContext: string;
  /** The rashi (sign) sitting on this box for this member. */
  sign: string;
  /** Primary display direction for the box's sign. */
  direction: string;
  /** Sign lord abbreviation (e.g. 'Ma'), when supplied. */
  lord?: string;
  /** Full Set-7 display direction list for the box's sign. */
  directions?: string[];
}

/** One family member's astro row. */
export interface AstroMember {
  personId: string;
  name: string;
  /** Free-text relationship to the client (e.g. 'Husband · father'). */
  relationToClient: string;
  isBreadwinner: boolean;
  /** Ascendant sign (English), null when the chart is still pending. */
  ascendantSign: string | null;
  /** Optional Sanskrit ascendant label (e.g. 'Simha'), when supplied. */
  ascendantSanskrit?: string | null;
  chartStatus: ChartStatus;
  /** Ideal + positive zones from the Set-9 port. Empty while chart pending. */
  idealZones: AstroIdealZone[];
  /** Best zone achievable in THIS home vs the marked bedrooms; null if none. */
  achievableZone: AstroAchievableZone | null;
  /** The 12-row Comparative Box Matrix; empty while the chart is pending. */
  boxMatrix: AstroBoxRow[];
  /** Chart's ideal entrance verdict; null while the chart is pending. */
  entranceVerdict: AstroEntranceVerdict | null;
}

/**
 * One row of the who-sleeps-where allocation. Forge returns a COMBINED row for
 * a married couple (both share one room), NOT one row per person — so the row
 * carries the full occupant set, not a single member.
 */
export interface AstroAllocationRow {
  /** Scalar convenience id — the self / first member of the row. */
  personId: string;
  /** Every occupant id sharing this room (a couple = 2). */
  personIds: string[];
  /** Every occupant display name — join for the "who" line ("Rajesh & Priya"). */
  members: string[];
  /** The FLOOR label (e.g. 'Ground Floor'); null when the engine omits it. */
  room: string | null;
  /** The marked bedroom zone this room occupies (from Per-property). */
  zone: string;
  /** 'engine' = the drafted allocation; 'override' = an Acharya pin. */
  source: 'engine' | 'override';
  /** Optional one-line rationale ("why"), if the engine supplies one. */
  note?: string | null;
  /** True when this row is a best-available compromise (flag it visually). */
  flagged?: boolean;
}

/** The resolved Astro-Vastu view for one project. */
export interface AstroVastuResolved {
  familyId: string | null;
  /** The bedroom zones marked during Per-property, e.g. ['SW','NW','NE']. */
  markedBedroomZones: string[];
  members: AstroMember[];
  allocation: AstroAllocationRow[];
  /** ISO timestamp the backend computed this view. */
  generatedAt: string | null;
}

/* ════════════════════ The callable ════════════════════ */

/**
 * The raw wire response — every field optional so a partial payload from an
 * in-flight backend contract never trips a runtime error. The normaliser owns
 * all the coercion.
 */
interface RawResolveResponse {
  familyId?: string | null;
  markedBedroomZones?: unknown;
  members?: unknown;
  allocation?: unknown;
  generatedAt?: string | null;
}

const _resolveAstroVastu = httpsCallable<
  { projectId: string },
  RawResolveResponse
>(functions, 'resolveAstroVastuForProject');

/* ── defensive coercion helpers ── */

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function bool(v: unknown): boolean {
  return v === true;
}
function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function normalizeIdealZone(v: unknown): AstroIdealZone | null {
  if (!isObj(v)) return null;
  const zone = str(v.zone);
  if (!zone) return null;
  const klass = v.class === 'positive' || v.klass === 'positive' ? 'positive' : 'ideal';
  return { zone, klass };
}

function normalizeAchievable(v: unknown): AstroAchievableZone | null {
  if (!isObj(v)) return null;
  const zone = str(v.zone);
  if (!zone) return null;
  return { zone, severity: str(v.severity, 'ideal') };
}

function normalizePada(v: unknown): AstroEntrancePada | null {
  if (!isObj(v)) return null;
  const pada = strOrNull(v.pada);
  const zone = strOrNull(v.zone);
  // A row with neither a pada nor a zone carries nothing to show — drop it.
  if (!pada && !zone) return null;
  return { pada, zone, box: numOrNull(v.box), rashi: strOrNull(v.rashi) };
}

function normalizeEntranceVerdict(v: unknown): AstroEntranceVerdict | null {
  // Only build a verdict when the engine actually sends a disposition — a null
  // (chart-pending) member degrades to the "pending" placeholder.
  if (!isObj(v)) return null;
  const disposition = str(v.disposition);
  if (!disposition) return null;
  return {
    disposition,
    note: strOrNull(v.note),
    recommendationZone: strOrNull(v.recommendationZone),
    recommendationPada: strOrNull(v.recommendationPada),
    recommendationBox: numOrNull(v.recommendationBox),
    acceptablePadas: arr(v.acceptablePadas)
      .map(normalizePada)
      .filter((p): p is AstroEntrancePada => p !== null),
    avoidPadas: arr(v.avoidPadas)
      .map(normalizePada)
      .filter((p): p is AstroEntrancePada => p !== null),
    noIdealReason: strOrNull(v.noIdealReason),
    source: str(v.source) || undefined,
  };
}

function normalizeBoxRow(v: unknown): AstroBoxRow | null {
  if (!isObj(v)) return null;
  const box = num(v.box, 0);
  // Box must be 1..12 — a 0/absent box is unusable.
  if (!box) return null;
  return {
    box,
    lifeContext: str(v.lifeContext),
    sign: str(v.sign),
    direction: str(v.direction),
    lord: str(v.lord) || undefined,
    directions: arr(v.directions)
      .map((x) => str(x))
      .filter((s) => s.length > 0),
  };
}

function normalizeMember(v: unknown): AstroMember | null {
  if (!isObj(v)) return null;
  const personId = str(v.personId);
  const name = str(v.name);
  // A row with neither an id nor a name is unusable — drop it rather than
  // render a blank card.
  if (!personId && !name) return null;
  const chartStatus: ChartStatus = v.chartStatus === 'resolved' ? 'resolved' : 'pending';
  return {
    personId: personId || name,
    name: name || personId,
    relationToClient: str(v.relationToClient),
    isBreadwinner: bool(v.isBreadwinner),
    ascendantSign: strOrNull(v.ascendantSign),
    ascendantSanskrit: strOrNull(v.ascendantSanskrit),
    chartStatus,
    idealZones: arr(v.idealZones)
      .map(normalizeIdealZone)
      .filter((z): z is AstroIdealZone => z !== null),
    achievableZone: normalizeAchievable(v.achievableZone),
    boxMatrix: arr(v.boxMatrix)
      .map(normalizeBoxRow)
      .filter((b): b is AstroBoxRow => b !== null),
    entranceVerdict: normalizeEntranceVerdict(v.entranceVerdict),
  };
}

function normalizeAllocation(v: unknown): AstroAllocationRow | null {
  if (!isObj(v)) return null;
  const zone = str(v.zone);
  const members = arr(v.members).map((x) => str(x)).filter((s) => s.length > 0);
  const personIds = arr(v.personIds).map((x) => str(x)).filter((s) => s.length > 0);
  // Usable only if there is a zone OR at least one occupant to show.
  if (!zone && members.length === 0 && personIds.length === 0) return null;
  return {
    personId: str(v.personId),
    personIds,
    members,
    room: strOrNull(v.room), // FLOOR label; may legitimately be null
    zone,
    source: v.source === 'override' ? 'override' : 'engine',
    note: strOrNull(v.note),
    flagged: bool(v.flagged),
  };
}

/** Coerce any wire payload into a total, safe `AstroVastuResolved`. */
export function normalizeResolveResponse(raw: RawResolveResponse | null | undefined): AstroVastuResolved {
  const r = raw ?? {};
  return {
    familyId: strOrNull(r.familyId),
    markedBedroomZones: arr(r.markedBedroomZones)
      .map((z) => str(z))
      .filter((z) => z.length > 0),
    members: arr(r.members)
      .map(normalizeMember)
      .filter((m): m is AstroMember => m !== null),
    allocation: arr(r.allocation)
      .map(normalizeAllocation)
      .filter((a): a is AstroAllocationRow => a !== null),
    generatedAt: strOrNull(r.generatedAt),
  };
}

/**
 * Resolve the Astro-Vastu view for a project. Returns `null` on ANY failure
 * (network, auth, backend error, or a not-yet-deployed callable) so the screen
 * can render a clean empty/error state instead of throwing. Never rejects.
 */
export async function resolveAstroVastuForProject(
  projectId: string,
): Promise<AstroVastuResolved | null> {
  if (!projectId) return null;
  try {
    const result = await _resolveAstroVastu({ projectId });
    return normalizeResolveResponse(result.data);
  } catch {
    // The callable may not be deployed yet (Forge is finalising it in parallel).
    // Degrade to null — the screen shows "astro inputs not resolved yet", never
    // a crash.
    return null;
  }
}

/* ════════════════════ Linked-Family seam (Phase 1) ═══════════════════════════
 *
 * Two staff-gated callables (region asia-south1, same client instance) that let
 * staff attach an EXISTING astro family to a project — which is what makes the
 * Astro-Vastu tab resolve. Unlike `resolveAstroVastuForProject` above (which
 * degrades to null), these two REJECT on failure so the "Linked Family" box can
 * distinguish loading / empty / error and show a staff-friendly message.
 * ──────────────────────────────────────────────────────────────────────────── */

/** One selectable astro family (camelCased from the backend's snake_case row). */
export interface AstroFamilyOption {
  id: string;
  familyLabel: string;
  memberCount: number;
  primaryOwnerId: string | null;
}

/** Result of a link / unlink write. `astroFamilyId: null` ⇒ the link was cleared. */
export interface LinkAstroFamilyResult {
  ok: boolean;
  astroFamilyId: string | null;
  familyLabel: string | null;
  memberCount: number | null;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function normalizeFamilyOption(v: unknown): AstroFamilyOption | null {
  if (!isObj(v)) return null;
  const id = str(v.id);
  if (!id) return null;
  return {
    id,
    // Accept either casing so a wire-shape tweak can't blank the label.
    familyLabel: str(v.family_label) || str(v.familyLabel) || id,
    memberCount: num(v.member_count ?? v.memberCount, 0),
    primaryOwnerId: strOrNull(v.primary_owner_id ?? v.primaryOwnerId),
  };
}

const _listAstroFamilies = httpsCallable<
  Record<string, never>,
  { families?: unknown }
>(functions, 'listAstroFamilies');

/**
 * List every astro family in the team's astro store. Resolves `[]` when there
 * are genuinely no families (clean empty state); REJECTS on network/auth/backend
 * failure so the caller can render an error state distinct from "no families".
 */
export async function listAstroFamilies(): Promise<AstroFamilyOption[]> {
  const res = await _listAstroFamilies({});
  // Wire shape is HttpsCallableResult → payload at `.data` (matches
  // resolveAstroVastuForProject above). Backend returns `{ families: [...] }`.
  const raw = isObj(res.data) ? res.data.families : undefined;
  return arr(raw)
    .map(normalizeFamilyOption)
    .filter((f): f is AstroFamilyOption => f !== null);
}

const _linkProjectAstroFamily = httpsCallable<
  { projectId: string; astroFamilyId: string | null },
  { ok?: boolean; astro_family_id?: unknown; family_label?: unknown; member_count?: unknown }
>(functions, 'linkProjectAstroFamily');

/**
 * Link (or, with `astroFamilyId: null`, unlink) an astro family on a project.
 * Returns the authoritative linked state the backend persisted. REJECTS on
 * failure so the box can toast a staff-friendly error and NOT commit optimism.
 */
export async function linkProjectAstroFamily(
  projectId: string,
  astroFamilyId: string | null,
): Promise<LinkAstroFamilyResult> {
  const res = await _linkProjectAstroFamily({ projectId, astroFamilyId });
  const d = (res.data ?? {}) as Record<string, unknown>;
  return {
    ok: d.ok === true,
    astroFamilyId: strOrNull(d.astro_family_id),
    familyLabel: strOrNull(d.family_label),
    memberCount: typeof d.member_count === 'number' ? d.member_count : null,
  };
}

/* ════════════════════ Linked-Contacts seam (Piece 1 · step ⑤) ════════════════
 *
 * The EITHER/OR partner to `linkProjectAstroFamily`. Staff attach 1–2 individual
 * astro persons (derived from Contacts) to a project when its astro subject is a
 * couple / individual rather than a full Group. Mirrors `linkProjectAstroFamily`
 * exactly (staff-gated, asia-south1, same client instance, REJECTS on failure so
 * the "Linked Contacts" box can distinguish loading / empty / error). Setting
 * persons CLEARS the group link server-side; passing `null` clears the persons.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Result of a link / unlink write. `astroPersonIds: null` (or `[]`) ⇒ the
 * persons link was cleared. `names` is the authoritative display cache the
 * backend persisted alongside the ids (parallel to `astroPersonIds`, same order).
 */
export interface LinkAstroPersonsResult {
  ok: boolean;
  astroPersonIds: string[] | null;
  names: string[];
  count: number;
}

/* ════════════════════ Families-WITH-members seam (Groups sync) ════════════════
 *
 * The roster-carrying sibling of `listAstroFamilies`. Where that callable returns
 * only the family SHELL (label + count), this one returns each family together
 * with its member persons so `reconcileAstroFamiliesIntoGroups` can create the
 * member Contacts and populate the Group's roster (owner: "bring the group along
 * with its contacts"). Same staff gate, region (asia-south1) and client instance.
 * REJECTS on failure so the "Sync from Astro" action can surface an error state
 * distinct from "no families" (mirrors `listAstroFamilies`).
 * ──────────────────────────────────────────────────────────────────────────── */

/** One member person of an astro family (camelCased from the callable's row). */
export interface AstroFamilyMember {
  /** Stable astro person id — the Contact dedup key (`astroPersonId`). */
  personId: string;
  displayName: string;
  birthDate: string | null;
  birthTime: string | null;
  placeCity: string | null;
  placeState: string | null;
  placeCountry: string | null;
  birthLat: number | null;
  birthLon: number | null;
  birthTzName: string | null;
  relationshipType: string | null;
  isBreadwinner: boolean;
  gender: string | null;
}

/** One astro family together with its member persons. */
export interface AstroFamilyWithMembers {
  id: string;
  familyLabel: string;
  primaryOwnerId: string | null;
  members: AstroFamilyMember[];
}

function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function normalizeFamilyMember(v: unknown): AstroFamilyMember | null {
  if (!isObj(v)) return null;
  // Accept either casing so a wire-shape tweak can't drop a field. The backend
  // contract is snake_case; we tolerate camelCase too.
  const personId = str(v.personId ?? v.person_id);
  const displayName = str(v.display_name ?? v.displayName);
  // A member with no id AND no name is unusable — nothing to dedup or show.
  if (!personId && !displayName) return null;
  return {
    personId: personId || displayName,
    displayName: displayName || personId,
    birthDate: strOrNull(v.birth_date ?? v.birthDate),
    birthTime: strOrNull(v.birth_time ?? v.birthTime),
    placeCity: strOrNull(v.place_city ?? v.placeCity),
    placeState: strOrNull(v.place_state ?? v.placeState),
    placeCountry: strOrNull(v.place_country ?? v.placeCountry),
    birthLat: numOrNull(v.birth_lat ?? v.birthLat),
    birthLon: numOrNull(v.birth_lon ?? v.birthLon),
    birthTzName: strOrNull(v.birth_tz_name ?? v.birthTzName),
    relationshipType: strOrNull(v.relationship_type ?? v.relationshipType),
    isBreadwinner: bool(v.is_breadwinner ?? v.isBreadwinner),
    gender: strOrNull(v.gender),
  };
}

function normalizeFamilyWithMembers(v: unknown): AstroFamilyWithMembers | null {
  if (!isObj(v)) return null;
  const id = str(v.id);
  if (!id) return null;
  return {
    id,
    familyLabel: str(v.family_label) || str(v.familyLabel) || id,
    primaryOwnerId: strOrNull(v.primary_owner_id ?? v.primaryOwnerId),
    members: arr(v.members)
      .map(normalizeFamilyMember)
      .filter((m): m is AstroFamilyMember => m !== null),
  };
}

const _getAstroFamiliesWithMembers = httpsCallable<
  Record<string, never>,
  { families?: unknown }
>(functions, 'getAstroFamiliesWithMembers');

/**
 * List every astro family WITH its member persons. Resolves `[]` when there are
 * genuinely no families; REJECTS on network/auth/backend failure so the caller
 * can render an error state distinct from "no families" (mirrors
 * `listAstroFamilies`).
 */
export async function getAstroFamiliesWithMembers(): Promise<AstroFamilyWithMembers[]> {
  const res = await _getAstroFamiliesWithMembers({});
  const raw = isObj(res.data) ? res.data.families : undefined;
  return arr(raw)
    .map(normalizeFamilyWithMembers)
    .filter((f): f is AstroFamilyWithMembers => f !== null);
}

const _linkProjectAstroPersons = httpsCallable<
  { projectId: string; personIds: string[] | null },
  { ok?: boolean; astro_person_ids?: unknown; names?: unknown; count?: unknown }
>(functions, 'linkProjectAstroPersons');

/**
 * Link (or, with `personIds: null`, unlink) 1–2 individual astro persons on a
 * project. Returns the authoritative linked state the backend persisted. REJECTS
 * on failure so the box can toast a staff-friendly error and NOT commit optimism.
 * Setting persons clears the group link (`astro_family_id`) server-side.
 */
export async function linkProjectAstroPersons(
  projectId: string,
  personIds: string[] | null,
): Promise<LinkAstroPersonsResult> {
  const res = await _linkProjectAstroPersons({ projectId, personIds });
  const d = (res.data ?? {}) as Record<string, unknown>;
  const ids = arr(d.astro_person_ids)
    .map((x) => str(x))
    .filter((s) => s.length > 0);
  const names = arr(d.names)
    .map((x) => str(x))
    .filter((s) => s.length > 0);
  return {
    ok: d.ok === true,
    // A cleared link comes back as an empty array — normalise to null so callers
    // can treat "no persons" identically whether the backend sent [] or omitted it.
    astroPersonIds: ids.length > 0 ? ids : null,
    names,
    count: typeof d.count === 'number' && Number.isFinite(d.count) ? d.count : ids.length,
  };
}
